<template>
  <div
    ref="rootEl"
    class="mb-2 flex h-full w-full items-center justify-center gap-2 pl-4 pr-10"
    :style="inputThemeVars">
    <span class="min-w-0 flex-grow truncate text-left font-medium text-black">
      {{ usageTitle }}
    </span>
    <div class="flex items-center gap-4" @mousedown.stop @pointerdown.stop>
      <div class="flex items-center gap-1">
        <input
          v-model="ratioDraft"
          type="number"
          :min="minRatio"
          max="100"
          step="1"
          class="core-form-input ratio-box mb-0"
          @change="commitRatioDraft"
          @blur="commitRatioDraft"
          @keydown.enter.prevent="commitRatioDraft" />
        <span class="whitespace-nowrap">%</span>
      </div>
      <div v-if="showAreaInput && roundedAreaValue != null" class="flex items-center gap-1">
        <input
          v-model="areaDraft"
          type="number"
          :min="minArea"
          :max="quantityArea"
          step="1"
          class="core-form-input area-box mb-0"
          @change="commitAreaDraft"
          @blur="commitAreaDraft"
          @keydown.enter.prevent="commitAreaDraft" />
        <span class="whitespace-nowrap">{{ $t('general.sq_m') }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUpdated, ref, watch } from 'vue'
import type { SharedUsageItem, UsageItem } from '@/schemas/general/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import { useContentStore } from '@/stores/content'

const props = defineProps<{
  usageItem: UsageItem | SharedUsageItem
  usageItems: Array<UsageItem | SharedUsageItem>
  usageIndex: number
  ratio: number
  quantityArea?: number
  onCommitRatio?: (value: number) => boolean
  onCommitArea?: (value: number) => boolean
  minRatio?: number
  minArea?: number
  showAreaInput?: boolean
  titleResolver?: (usageItem: UsageItem | SharedUsageItem, usageIndex: number) => string
}>()

const { wt } = useWidgetText('widget-texts-project-general')
const contentStore = useContentStore()

const minRatio = computed(() => props.minRatio ?? 1)
const minArea = computed(() => props.minArea ?? 1)
const showAreaInput = computed(() => props.showAreaInput ?? true)

const usageTitle = computed(() => {
  const resolvedTitle = props.titleResolver?.(props.usageItem, props.usageIndex)
  if (resolvedTitle) return resolvedTitle

  return getUsageDisplayTitle(
    props.usageItem as UsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null },
    wt,
    {
      allUsages: props.usageItems as Array<
        UsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null }
      >,
      index: props.usageIndex,
      resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
    }
  )
})

const roundedAreaValue = computed(() => {
  if (!props.quantityArea || props.quantityArea <= 0) return null
  return (props.quantityArea * props.ratio) / 100
})

const ratioDraft = ref('')
const areaDraft = ref('')
const rootEl = ref<HTMLElement | null>(null)
const useLightInputText = ref(true)

const inputThemeVars = computed(() => {
  if (useLightInputText.value) {
    return {
      '--ratio-input-text-color': '#fafafa',
      '--ratio-input-bg-color': 'rgba(255, 255, 255, 0.12)',
      '--ratio-input-border-color': 'rgba(255, 255, 255, 0.18)',
    }
  }

  return {
    '--ratio-input-text-color': '#111827',
    '--ratio-input-bg-color': '#ffffff',
    '--ratio-input-border-color': '#d4d4d4',
  }
})

function syncDrafts(): void {
  ratioDraft.value = String(Math.round(props.ratio))
  areaDraft.value = roundedAreaValue.value != null ? String(Math.round(roundedAreaValue.value)) : ''
}

function parseNumber(raw: string, minValue: number): number | null {
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed < minValue) return null
  return parsed
}

function commitRatioDraft(): void {
  const parsed = parseNumber(ratioDraft.value, minRatio.value)
  if (parsed == null || parsed > 100) {
    syncDrafts()
    return
  }
  if (!props.onCommitRatio || !props.onCommitRatio(parsed)) {
    syncDrafts()
  }
}

function commitAreaDraft(): void {
  if (!props.quantityArea || props.quantityArea <= 0 || roundedAreaValue.value == null) {
    syncDrafts()
    return
  }

  const parsed = parseNumber(areaDraft.value, minArea.value)
  if (parsed == null || parsed > props.quantityArea) {
    syncDrafts()
    return
  }

  if (!props.onCommitArea || !props.onCommitArea(parsed)) {
    syncDrafts()
  }
}

function updateInputThemeFromPaneBackground(): void {
  if (typeof window === 'undefined') return
  const element = rootEl.value
  if (!element) return

  const pane = element.closest('.core-pane') as HTMLElement | null
  const backgroundSource = pane ?? element
  const rgb = readBackgroundRgb(backgroundSource)
  if (!rgb) return

  useLightInputText.value = getRelativeLuminance(rgb) < 0.45
}

function readBackgroundRgb(element: HTMLElement): [number, number, number] | null {
  let current: HTMLElement | null = element
  while (current) {
    const parsed = parseCssColor(window.getComputedStyle(current).backgroundColor)
    if (parsed && parsed.alpha > 0) {
      return [parsed.r, parsed.g, parsed.b]
    }
    current = current.parentElement
  }
  return null
}

function parseCssColor(color: string): { r: number; g: number; b: number; alpha: number } | null {
  const rgbaMatch = color.match(/rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)/i)
  if (!rgbaMatch) return null

  return {
    r: Number(rgbaMatch[1]),
    g: Number(rgbaMatch[2]),
    b: Number(rgbaMatch[3]),
    alpha: rgbaMatch[4] == null ? 1 : Number(rgbaMatch[4]),
  }
}

function getRelativeLuminance([r, g, b]: [number, number, number]): number {
  const toLinear = (channel: number): number => {
    const normalized = channel / 255
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4
  }

  const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)]
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb
}

watch(
  () => [props.ratio, props.quantityArea],
  () => {
    syncDrafts()
  },
  { immediate: true }
)

onMounted(() => {
  updateInputThemeFromPaneBackground()
})

onUpdated(() => {
  updateInputThemeFromPaneBackground()
})
</script>

<style lang="postcss" scoped>
.ratio-box {
  width: 4.25rem;
}

.area-box {
  width: 5.5rem;
}

.ratio-box,
.area-box {
  color: var(--ratio-input-text-color);
  background-color: var(--ratio-input-bg-color);
  border-color: var(--ratio-input-border-color);
  text-align: right;
  box-sizing: border-box;
  padding-left: 0.25rem;
  padding-right: 0.5rem;
}

.ratio-box::-webkit-inner-spin-button,
.area-box::-webkit-inner-spin-button {
  margin-left: 0.25rem;
}
</style>
